import { istDayOf } from '../lib/ist'

/**
 * Calendar-anchored periods for the console's date picker (Figma K15b): "Week
 * to date", "Month to date", "This term" and "This academic year" — spans whose
 * START is a calendar fact rather than a count of days back.
 *
 * 🔴 EVERY SPAN ENDS ON TODAY IN INDIA (`istDayOf(now)`), like `presetSpan`.
 * A Monday that is still Sunday in UTC (00:00–05:30 IST) starts a new week
 * here, because the school it describes is already in that Monday.
 *
 * ⚠️ DAY ARITHMETIC IS ON `YYYY-MM-DD` STRINGS READ AS UTC MIDNIGHTS, never on
 * the browser's local `Date` parts. A day key has no zone; reading it in UTC
 * keeps "the Monday of this week" the same on a laptop in India, London or
 * California.
 *
 * Pure — the screens and `hashRange` resolve a stored `wtd`/`mtd`/`term`/`ay`
 * through `anchoredSpan`, so a bookmarked "This term" is this term next month.
 */

export type RangeAnchor = 'wtd' | 'mtd' | 'term' | 'ay'

export const RANGE_ANCHORS: readonly RangeAnchor[] = ['wtd', 'mtd', 'term', 'ay']

export function isRangeAnchor(v: unknown): v is RangeAnchor {
  return typeof v === 'string' && (RANGE_ANCHORS as readonly string[]).includes(v)
}

/** One term of a school year, `YYYY-MM-DD` inclusive both ends. */
export interface AcademicTerm {
  name: string
  startsOn: string
  endsOn: string
}

/**
 * How a school year is laid out. `terms` absent means the default terms
 * derived from `yearStartMonth` (two five-month terms and a two-month break).
 */
export interface AcademicCalendar {
  /** 1–12; June in most Indian schools. */
  yearStartMonth: number
  terms?: readonly AcademicTerm[]
}

/**
 * D21 — the academic year starts in June; Term 1 is June–October and Term 2
 * November–March (April and May are the break). A school's own terms
 * (`AcademicTerm` rows, SCHOOL's `GET /school/terms`) replace these when a
 * screen has them; the operator console has no one school's calendar, so it
 * reads the defaults.
 */
export const DEFAULT_ACADEMIC: AcademicCalendar = { yearStartMonth: 6 }

const DAY_MS = 86_400_000
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function dayUtc(day: string): Date {
  const m = DAY_RE.exec(day)
  return new Date(Date.UTC(Number(m?.[1] ?? 1970), Number(m?.[2] ?? 1) - 1, Number(m?.[3] ?? 1)))
}

function utcDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function addDays(day: string, n: number): string {
  return utcDay(new Date(dayUtc(day).getTime() + n * DAY_MS))
}

/** The last day of `month` (1–12) in `year`, as `YYYY-MM-DD`. */
function lastOfMonth(year: number, month: number): string {
  return utcDay(new Date(Date.UTC(year, month, 0)))
}

/** `year`/`month` moved by `by` months, month kept in 1–12. */
function shiftMonth(year: number, month: number, by: number): { year: number; month: number } {
  const i = year * 12 + (month - 1) + by
  return { year: Math.floor(i / 12), month: (i % 12) + 1 }
}

/** Days from `from` to `to`, both inclusive (`2026-10-01`..`2026-10-01` is 1). */
export function spanDays(from: string, to: string): number {
  return Math.round((dayUtc(to).getTime() - dayUtc(from).getTime()) / DAY_MS) + 1
}

/** The first day of the academic year that contains `today`. */
export function academicYearStart(today: string, cal: AcademicCalendar = DEFAULT_ACADEMIC): string {
  const y = Number(today.slice(0, 4))
  const m = Number(today.slice(5, 7))
  const start = Math.min(12, Math.max(1, Math.round(cal.yearStartMonth)))
  return `${String(m >= start ? y : y - 1)}-${pad(start)}-01`
}

/**
 * The default terms of the academic year containing `today`: Term 1 the first
 * five months, Term 2 the next five (Jun–Oct and Nov–Mar for a June start).
 */
export function defaultTerms(today: string, cal: AcademicCalendar = DEFAULT_ACADEMIC): AcademicTerm[] {
  const ay = academicYearStart(today, cal)
  const y = Number(ay.slice(0, 4))
  const m = Number(ay.slice(5, 7))
  const t1End = shiftMonth(y, m, 4)
  const t2Start = shiftMonth(y, m, 5)
  const t2End = shiftMonth(y, m, 9)
  return [
    { name: 'Term 1', startsOn: ay, endsOn: lastOfMonth(t1End.year, t1End.month) },
    { name: 'Term 2', startsOn: `${String(t2Start.year)}-${pad(t2Start.month)}-01`, endsOn: lastOfMonth(t2End.year, t2End.month) },
  ]
}

/**
 * The term `today` belongs to: the one containing it, else — between terms —
 * the latest one that has already started. Null when no term has started yet
 * (a school whose first term is still ahead).
 */
export function currentTerm(today: string, cal: AcademicCalendar = DEFAULT_ACADEMIC): AcademicTerm | null {
  const terms = [...(cal.terms ?? defaultTerms(today, cal))]
    .filter((t) => DAY_RE.test(t.startsOn) && DAY_RE.test(t.endsOn))
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))
  const started = terms.filter((t) => t.startsOn <= today)
  const containing = started.filter((t) => t.endsOn >= today)
  return containing[containing.length - 1] ?? started[started.length - 1] ?? null
}

/**
 * An anchored period as an inclusive `{ from, to }` of IST days.
 *
 * - `wtd`  Monday of this IST week → today
 * - `mtd`  the 1st of this IST month → today
 * - `ay`   the 1st of `yearStartMonth` on or before today → today
 * - `term` the current term (see `currentTerm`), its end clipped to today;
 *          between terms that is the whole of the term just finished.
 *          ⚠️ With no term started at all it falls back to the academic year,
 *          so the picker never offers an empty or future span.
 */
export function anchoredSpan(anchor: RangeAnchor, now: Date = new Date(), cal: AcademicCalendar = DEFAULT_ACADEMIC): { from: string; to: string } {
  const today = istDayOf(now)
  switch (anchor) {
    case 'wtd': {
      const weekday = dayUtc(today).getUTCDay() // 0 = Sunday
      return { from: addDays(today, -((weekday + 6) % 7)), to: today }
    }
    case 'mtd':
      return { from: `${today.slice(0, 8)}01`, to: today }
    case 'ay':
      return { from: academicYearStart(today, cal), to: today }
    case 'term': {
      const t = currentTerm(today, cal)
      if (!t) return { from: academicYearStart(today, cal), to: today }
      return { from: t.startsOn, to: t.endsOn < today ? t.endsOn : today }
    }
  }
}
